/**
 * Manages two independent countdown timers:
 * - Ending countdown: fires when the AI initiates a goodbye, auto-confirms after reaching zero.
 * - Inactivity countdown: fires when the user has been silent too long, auto-disconnects at zero.
 *
 * Both countdowns notify the live-session worker of cancellations so the server
 * can reset its own timers.
 */

import { useState, useRef, useCallback } from 'react';

interface UseCountdownsOptions {
  wsRef: React.MutableRefObject<WebSocket | null>;
}

export function useCountdowns({ wsRef }: UseCountdownsOptions) {
  const [endingCountdown, setEndingCountdown] = useState<number | null>(null);
  const endingCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [inactivityCountdown, setInactivityCountdown] = useState<number | null>(null);
  const [inactivityTotalSeconds, setInactivityTotalSeconds] = useState<number>(30);
  const inactivityCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Start the ending countdown. Sends `confirm_ending` to the worker when it reaches zero. */
  const startEndingCountdown = useCallback((seconds: number) => {
    if (endingCountdownRef.current) {
      clearInterval(endingCountdownRef.current);
    }

    setEndingCountdown(seconds);

    endingCountdownRef.current = setInterval(() => {
      setEndingCountdown(prev => {
        if (prev === null || prev <= 1) {
          if (endingCountdownRef.current) {
            clearInterval(endingCountdownRef.current);
            endingCountdownRef.current = null;
          }
          // Send confirm_ending to worker
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'confirm_ending' }));
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [wsRef]);

  const cancelEndingCountdown = useCallback(() => {
    if (endingCountdownRef.current) {
      clearInterval(endingCountdownRef.current);
      endingCountdownRef.current = null;
    }
    setEndingCountdown(null);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel_ending' }));
    }
  }, [wsRef]);

  const startInactivityCountdown = useCallback((seconds: number) => {
    if (inactivityCountdownRef.current) {
      clearInterval(inactivityCountdownRef.current);
    }

    setInactivityTotalSeconds(seconds);
    setInactivityCountdown(seconds);

    inactivityCountdownRef.current = setInterval(() => {
      setInactivityCountdown(prev => {
        if (prev === null || prev <= 1) {
          if (inactivityCountdownRef.current) {
            clearInterval(inactivityCountdownRef.current);
            inactivityCountdownRef.current = null;
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const cancelInactivityCountdown = useCallback(() => {
    if (inactivityCountdownRef.current) {
      clearInterval(inactivityCountdownRef.current);
      inactivityCountdownRef.current = null;
    }
    setInactivityCountdown(null);
  }, []);

  /** Cancel inactivity locally and notify the server so it resets its timer. */
  const handleCancelInactivity = useCallback(() => {
    cancelInactivityCountdown();
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel_inactivity' }));
    }
  }, [cancelInactivityCountdown, wsRef]);

  /** Clear the ending countdown without notifying the server (used when interview_ended arrives). */
  const clearEndingCountdown = useCallback(() => {
    if (endingCountdownRef.current) {
      clearInterval(endingCountdownRef.current);
      endingCountdownRef.current = null;
    }
    setEndingCountdown(null);
  }, []);

  const cleanupCountdowns = useCallback(() => {
    if (endingCountdownRef.current) {
      clearInterval(endingCountdownRef.current);
    }
    if (inactivityCountdownRef.current) {
      clearInterval(inactivityCountdownRef.current);
    }
  }, []);

  return {
    // State
    endingCountdown,
    inactivityCountdown,
    inactivityTotalSeconds,
    // Actions
    startEndingCountdown,
    cancelEndingCountdown,
    startInactivityCountdown,
    cancelInactivityCountdown,
    handleCancelInactivity,
    clearEndingCountdown,
    cleanupCountdowns,
  };
}
